import type { ReactNode } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface PanelEmptyStateProps {
  /** When omitted, only title and subtitle are shown (no icon badge). */
  icon?: IconDefinition;
  title: string;
  subtitle: string;
  testId?: string;
  /** Hide the icon tile (e.g. while Snake is open on Mission Control). */
  hideIcon?: boolean;
  children?: ReactNode;
}

/**
 * Centered empty-state chrome shared by Mission Control and Artefacts.
 * Matches the gradient icon tile + title/subtitle hierarchy from
 * ``AllClearState``.
 */
export function PanelEmptyState({
  icon,
  title,
  subtitle,
  testId,
  hideIcon = false,
  children,
}: PanelEmptyStateProps) {
  return (
    <div
      data-testid={testId}
      className="h-full flex flex-col items-center justify-center text-center px-6 py-16"
    >
      {icon && !hideIcon && (
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500/16 to-violet-500/16">
          <FontAwesomeIcon
            icon={icon}
            className="text-2xl text-indigo-600 dark:text-indigo-200/55"
          />
        </div>
      )}
      <h2 className="mb-1 text-base font-medium text-slate-900 dark:text-slate-100">{title}</h2>
      <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">{subtitle}</p>
      {children}
    </div>
  );
}
